import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { equipmentRatesApi, type EquipmentRate } from '@/lib/apiClient';

// ─── Rate Picker ──────────────────────────────────────────────────────────────
// Searchable equipment rate-sheet picker. Extracted from EquipmentPage so both
// the page (new/edit drawers, rate calculator) and the reusable
// EquipmentRequestForm can share it without a circular import.

export interface RatePickerProps {
  /** The current free-text or rate-derived equipment-type value to display in the input. */
  value: string;
  /** The currently selected rate row, if one was picked from the rate sheet. */
  selectedRate: EquipmentRate | null;
  /**
   * Called when the user picks a rate-sheet entry, types a custom value,
   * or clears the selection. `rate` is null in custom-text mode.
   */
  onChange: (next: { equipmentType: string; rate: EquipmentRate | null }) => void;
}

// Build the descriptive equipment-type string that gets persisted when a user
// picks a rate-sheet entry. Picking only the generic type (e.g. "Skid Steer")
// dropped the model/capacity the requester actually chose, which led to the
// wrong equipment being ordered — so carry the specifics: "S650 — Skid Steer · 74 hp".
function rateToTypeLabel(r: EquipmentRate): string {
  const base = `${r.model} — ${r.equipmentType}`;
  const spec = r.capacityRange ? `${r.capacityRange} ${r.unit ?? ''}`.trim() : '';
  return spec ? `${base} · ${spec}` : base;
}

// The line under the headline: category, then capacity.
//
// The headline already ends in the equipment type, so repeating an identical
// category directly beneath it is noise — "249 — Track Skid Steer" over
// "Track Skid Steer · 1400-1649 Lb". That happens on every row of the 2026
// sheet, which supplies one combined category/type column rather than the
// near-duplicate pair the older rows carry ("Scissor Lifts" / "Scissor Lift").
function rateSubline(r: EquipmentRate): string {
  const spec = r.capacityRange ? `${r.capacityRange} ${r.unit ?? ''}`.trim() : '';
  const category = r.category.trim().toLowerCase() === r.equipmentType.trim().toLowerCase()
    ? ''
    : r.category;
  return [category, spec].filter(Boolean).join(' · ');
}

export function RatePicker({ value, selectedRate, onChange }: RatePickerProps) {
  const [search, setSearch] = useState('');
  const [open, setOpen]     = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: rates = [], isLoading } = useQuery<EquipmentRate[]>({
    queryKey: ['equipment-rates', { activeOnly: true }],
    queryFn: () => equipmentRatesApi.list({ activeOnly: true }),
    staleTime: 10 * 60 * 1000,
  });

  // Close popup when clicking outside.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rates;
    return rates.filter((r) =>
      r.model.toLowerCase().includes(q) ||
      r.equipmentType.toLowerCase().includes(q) ||
      r.category.toLowerCase().includes(q)
    );
  }, [rates, search]);

  const fmt = (n: number | null) => n != null
    ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 })
    : '—';

  const inp: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px', fontSize: 13, width: '100%', fontFamily: 'inherit', background: '#fff', color: 'var(--text)' };

  // Selected-rate "chip" view
  if (selectedRate) {
    return (
      <div style={{ border: '1px solid var(--ew)', borderRadius: 'var(--ew-r-md)', padding: '8px 10px', background: 'var(--ew-dim, #f0fdf4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
              {selectedRate.model} — {selectedRate.equipmentType}
            </div>
            <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 1 }}>
              {rateSubline(selectedRate)}
            </div>
          </div>
          <button type="button" onClick={() => { setSearch(''); onChange({ equipmentType: '', rate: null }); }}
            style={{ fontSize: 11, padding: '2px 8px', border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', background: '#fff', color: 'var(--gray)', cursor: 'pointer' }}>
            Change
          </button>
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 11, color: 'var(--gray)' }}>
          <span><strong style={{ color: 'var(--text)' }}>Day:</strong> {fmt(selectedRate.dayRate)}</span>
          <span><strong style={{ color: 'var(--text)' }}>Week:</strong> {fmt(selectedRate.weekRate)}</span>
          <span><strong style={{ color: 'var(--text)' }}>Month:</strong> {fmt(selectedRate.monthRate)}</span>
          <span style={{ marginLeft: 'auto' }}>{selectedRate.rateYear} rate sheet</span>
        </div>
      </div>
    );
  }

  // Search-and-pick view (no rate selected — free text mode)
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        style={inp}
        value={open ? search : value}
        onFocus={() => { setSearch(value); setOpen(true); }}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
          // Treat typing as a free-text update of equipment type until the user picks.
          onChange({ equipmentType: e.target.value, rate: null });
        }}
        placeholder="Search model or equipment type… (e.g. Z-45/25 or Skid Steer)"
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          marginTop: 4, background: '#fff', border: '1px solid var(--border)',
          borderRadius: 'var(--ew-r-md)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          maxHeight: 280, overflowY: 'auto',
        }}>
          {isLoading && <div style={{ padding: 10, fontSize: 12, color: 'var(--gray)' }}>Loading rates…</div>}
          {!isLoading && filtered.length === 0 && (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--gray)' }}>
              No matches in the rate sheet — type a custom equipment type to use anyway.
            </div>
          )}
          {filtered.map((r) => (
            <div key={r.id}
              onMouseDown={(e) => {
                // mousedown so it fires before blur closes the popup
                e.preventDefault();
                onChange({ equipmentType: rateToTypeLabel(r), rate: r });
                setOpen(false);
                setSearch('');
              }}
              style={{ padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--ew-dim, #f0fdf4)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
            >
              <div style={{ fontSize: 13, fontWeight: 600 }}>{r.model} — {r.equipmentType}</div>
              <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 1, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span>{rateSubline(r)}</span>
                <span>D {fmt(r.dayRate)} / W {fmt(r.weekRate)} / M {fmt(r.monthRate)}</span>
              </div>
            </div>
          ))}
          {search.trim() && (
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                onChange({ equipmentType: search.trim(), rate: null });
                setOpen(false);
                setSearch('');
              }}
              style={{ padding: '8px 10px', cursor: 'pointer', background: 'var(--surface)', fontSize: 12, color: 'var(--gray)', fontStyle: 'italic' }}
            >
              Use custom: <strong style={{ color: 'var(--text)' }}>“{search.trim()}”</strong>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
